<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        @click="handleSubmit"
      >
        Save
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    Change your name
  </DialogHeader>
  <DialogContent>
    <div class="mb-5">
      <UiInput v-model="firstName" label="First name" />
    </div>
    <div>
      <UiInput v-model="lastName" label="Last name" />
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      primary
      :loading="loading"
      @click="handleSubmit"
    >
      Change name
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { UiButton, UiInput, Icon } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { ref } from 'vue'
import {
  useUpdateAccountNameMutation,
  type User,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'

const { close } = useDialog()
const toastStore = useToastStore()
const webviewStore = useWebviewStore()

const props = defineProps<{
  user: User
}>()

const firstName = ref(props.user.firstName)
const lastName = ref(props.user.lastName)

const { mutate: updateAccountName, loading } = useUpdateAccountNameMutation()

const handleSubmit = async () => {
  updateAccountName(
    {
      firstName: firstName.value,
      lastName: lastName.value,
    },
    {
      optimisticResponse: {
        __typename: 'Mutation',
        updateAccountName: {
          __typename: 'User',
          id: props.user.id,
          firstName: firstName.value,
          lastName: lastName.value,
          name: `${firstName.value} ${lastName.value}`,
        },
      },
      update() {
        if (webviewStore.isActive) {
          webviewStore.postMessage({
            type: NativeActionsEnum.UserUpdated,
            data: {
              user: {
                ...props.user,
                firstName: firstName.value,
                lastName: lastName.value,
                name: `${firstName.value} ${lastName.value}`,
              },
            },
          })
        }
      },
    }
  )

  toastStore.showToast({
    type: ToastType.Success,
    content: 'Your name has been updated',
  })

  close()
}
</script>
